import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Box,
  Button,
  SearchField,
  SortDirection,
  Table,
  Text,
} from '@factorialco/deprecated-design-system'
import { CanCreateTeamsDocument, TeamsListItemFragment } from 'generated/resources/graphql'
import orderBy from 'lodash/orderBy'

import { observer } from 'mobx-react'

import createComputed from 'modules/core/hooks/createComputed'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import usePagination from 'modules/core/hooks/usePagination'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'
import type { Analytic } from 'modules/core/store/models/Analytic'

import BlankSlateTeams from 'modules/teams/components/BlankSlateTeams'
import { isActiveMember } from 'modules/teams/lib/isActiveMember'
import teamsAnalytics from 'modules/teams/store/collections/teamsAnalytics'
import type { Attributes as TeamAnalyticsAttributes } from 'modules/teams/store/models/TeamAnalytics'

import TableHeader from './_Header'
import Item from './_Item'

interface Props {
  teams: TeamsListItemFragment[]
  loadingAnalytics: boolean
  canEmployeeSeeAggregatedPersonalInfo: boolean
  hasTeamsInsightsFeature: boolean
  hasClockInLocationEnabled: boolean
}

const useOrderTeams = createComputed(
  (teams: TeamsListItemFragment[], attribute: string, direction: SortDirection) => {
    const sortTeamAnalytics = (value?: Analytic<number | string>) =>
      value?.error ? 0 : value?.results?.[0]?.value

    const sort = (team: TeamsListItemFragment) => {
      const teamAnalytics = teamsAnalytics.find({ id: team.id })

      if (attribute === 'membershipsLength') {
        const activeMemberships = team.memberships.filter((m) => isActiveMember(m))
        return activeMemberships.length
      }

      return attribute === 'name'
        ? team[attribute as keyof TeamsListItemFragment]
        : sortTeamAnalytics(
            teamAnalytics?.attributes[
              attribute as keyof Pick<TeamAnalyticsAttributes, 'average_age' | 'tenure'>
            ]
          )
    }

    return orderBy(teams, sort, [direction])
  }
)

const filteredBySearchBox = createComputed((teams: TeamsListItemFragment[], search: string) =>
  teams.filter((team) => matchText(search, team.name))
)

const TeamsList = ({
  teams,
  loadingAnalytics,
  canEmployeeSeeAggregatedPersonalInfo,
  hasTeamsInsightsFeature,
  hasClockInLocationEnabled,
}: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { data } = useQuery(CanCreateTeamsDocument)

  const [sort, setSort] = useState<{ direction: SortDirection; attribute: string }>({
    direction: 'asc',
    attribute: 'name',
  })

  const orderedTeams = useOrderTeams(teams, sort.attribute, sort.direction).get()

  const canSeeAggregatedInfo = canEmployeeSeeAggregatedPersonalInfo
  const canCreateTeams = Boolean(data?.teams._policies.teamCreate.can)

  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const filteredTeams = filteredBySearchBox(orderedTeams, debouncedSearch).get()

  const { paginatedItems, loadMore, hasMore, page } = usePagination({
    batchSize: 50,
    items: filteredTeams,
  })

  const handleSortChange = (attribute: string, direction: SortDirection) => {
    setSort({ attribute, direction })
  }

  const tableOptions = () => (
    <Box width="full" flexDirection="row" alignItems="center" gap="s8">
      <SearchField
        variant="big"
        search={search}
        onSearch={onSearch}
        placeholder={i18n.t('common.search')}
      />
    </Box>
  )

  const tableOptionsBottomCopy = i18n.tp('employees.showing.items', {
    count: filteredTeams.length,
  })

  const tableOptionsBottom = () => (
    <Box width="full" key={filteredTeams.length}>
      <Text.Normal size="100" weight="medium">
        {tableOptionsBottomCopy}
      </Text.Normal>
    </Box>
  )

  useEffect(() => {
    track(trackingEvents.employee.page.team.visited)
  }, [])

  useEffect(() => {
    if (debouncedSearch) {
      track(trackingEvents.teams.searchTeamSubmit, {
        search_value: debouncedSearch,
      })
    }
  }, [debouncedSearch])

  const insightsColspan = canSeeAggregatedInfo ? 7 : 6
  const bodyColspan = hasTeamsInsightsFeature ? insightsColspan : 3

  return (
    <>
      <BlankSlateTeams show={!teams.length} />
      {!!teams.length && (
        <>
          <Box flexDirection="row" justifyContent="flexEnd" gap="s8">
            {canCreateTeams && (
              <>
                <Button
                  type="secondary"
                  iconName="upload"
                  onClick={() => {
                    track(trackingEvents.teams.importTeamsStart, {
                      from: 'teams_list',
                    })
                    navigate(navigation.employees.teams.importTeams.fullPath())
                  }}
                >
                  {i18n.t('teams.import_teams.title')}
                </Button>

                <Button
                  type="primary"
                  iconName="add"
                  onClick={() => navigate(navigation.employees.teams.list.add.fullPath())}
                >
                  {i18n.t('teams.add')}
                </Button>
              </>
            )}
          </Box>
          <Table options={tableOptions} fullWidth optionsBottom={tableOptionsBottom} bottomSticky>
            <TableHeader
              onSort={handleSortChange}
              sortAttribute={sort.attribute}
              sortDirection={sort.direction}
              canEmployeeSeeAggregatedPersonalInfo={canEmployeeSeeAggregatedPersonalInfo}
              hasTeamsInsightsFeature={hasTeamsInsightsFeature}
              hasClockInLocationEnabled={hasClockInLocationEnabled}
            />
            <Table.BodyWithInfiniteScroll
              page={page}
              loadMore={loadMore}
              hasMore={hasMore}
              colSpan={bodyColspan}
            >
              {paginatedItems.map((team, index) => (
                <Item
                  key={index}
                  team={team}
                  loadingAnalytics={loadingAnalytics}
                  canEmployeeSeeAggregatedPersonalInfo={canEmployeeSeeAggregatedPersonalInfo}
                  hasTeamsInsightsFeature={hasTeamsInsightsFeature}
                  hasClockInLocationEnabled={hasClockInLocationEnabled}
                />
              ))}
            </Table.BodyWithInfiniteScroll>
          </Table>
        </>
      )}
    </>
  )
}

export default observer(TeamsList)
